import type { AskQuestion } from './types';

import { locateQuotedSpan } from 'js/src/agent/tools/clip-segment-resolver';
import type { TranscriptWord } from 'js/src/client';
import { resolveClipSource } from 'js/src/components/app/chat-tab/components/ask-user-question/clip-preview';
import { retrieveTranscriptForAsset } from 'js/src/data/clip-creator/clip-creator-utils';

export type ClipSpan = { startMs: number; endMs: number; transcript: string };

export type ClipQuestionResolution = { ok: true; spans: ClipSpan[] } | { ok: false; error: string };

/**
 * Finds every clip of a `clip_select` question in the recording it is cut from, BEFORE the
 * question is shown, and returns each clip's time range and words so its preview can play exactly
 * that part of the file beside what is said in it.
 *
 * A clip is located by the quotes it opens and closes on, so a quote that tidies the recording up
 * (dropping a stutter or an "um") matches nothing. Catching that here turns it into an error the
 * agent can fix on the spot, instead of a card the user can't play.
 */
export async function resolveClipQuestion(question: AskQuestion): Promise<ClipQuestionResolution> {
    if (question.type !== 'clip_select') {
        return { ok: true, spans: [] };
    }
    const source = resolveClipSource(question.clip_source_media_id);
    if (!source) {
        return {
            ok: false,
            error: `${question.clip_source_media_id} is not a playable video or audio recording, so no clips could be cut from it. Pass the media id of the uploaded recording the clips come from — or, if it was only just uploaded, it may still be processing: wait a moment and ask again.`,
        };
    }

    let transcriptWords: TranscriptWord[] | undefined;
    try {
        ({ transcriptWords } = await retrieveTranscriptForAsset(source.assetUuid, source.isAudio));
    } catch {
        transcriptWords = undefined;
    }
    if (!transcriptWords || transcriptWords.length === 0) {
        return {
            ok: false,
            error: `the transcript of ${question.clip_source_media_id} couldn't be read yet, and each clip is found by its quotes in it. Wait a moment and try again.`,
        };
    }

    const spans: ClipSpan[] = [];
    const unresolved: string[] = [];
    for (const clip of question.clips) {
        const span = locateQuotedSpan(transcriptWords, clip);
        if (span.ok) {
            spans.push({
                startMs: transcriptWords[span.startIndex].startTime,
                endMs: transcriptWords[span.endIndex].endTime,
                transcript: transcriptWords
                    .slice(span.startIndex, span.endIndex + 1)
                    .map(({ word }) => word)
                    .join(' '),
            });
        } else {
            unresolved.push(`- "${clip.label}": ${span.error}`);
        }
    }
    if (unresolved.length > 0) {
        return {
            ok: false,
            error: `These clips could not be located in the recording, so the question was not shown:\n${unresolved.join('\n')}\nEach clip is found by its quotes, so copy them exactly as the transcript spells them — including filler words like "um" and any words the speaker repeats — and ask again.`,
        };
    }
    return { ok: true, spans };
}
